/**
 * Quiz questions typed or pasted as plain text (the editor's "Ro'yxatdan qo'shish" dialog), as a pure
 * function: text in, questions and line-numbered problems out.
 *
 *   1) «Kitob» ruscha qanday?      one question per line, optionally numbered "1)" or "1."
 *   *Книга                         its options on the lines below; the right one starts with *
 *   Тетрадь                        options may be lettered "A)", "b.", "В)" (all of them or none)
 *                                  a blank line between questions
 *
 * A numbered line inside a block also starts a new question, so a list pasted without blank lines
 * between its numbered questions still splits correctly.
 */

export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 6;

// "1)", "12." or "3 )" in front of a question; "1.5" is a number, not a numbering.
const QUESTION_NUMBER_RE = /^\d{1,3}\s*[.)](?!\d)\s*/;
// "A)", "b.", "В)" in front of an option: Latin A–F or Cyrillic А–Е, either case.
const OPTION_LETTER_RE = /^[A-Fa-fА-Еа-е]\s*[.)]\s*/;
const CORRECT_MARK = '*';

/** Error codes the dialog turns into sentences; every error carries the 1-based line it is about. */
export const BULK_ERRORS = {
  NO_OPTIONS: 'noOptions',
  MISSING_QUESTION: 'missingQuestion',
  TOO_FEW_OPTIONS: 'tooFewOptions',
  TOO_MANY_OPTIONS: 'tooManyOptions',
  NO_CORRECT: 'noCorrect',
  MULTIPLE_CORRECT: 'multipleCorrect',
  EMPTY_OPTION: 'emptyOption',
};

function stripCorrectMark(text) {
  const trimmed = text.trim();
  if (!trimmed.startsWith(CORRECT_MARK)) return { text: trimmed, marked: false };
  return { text: trimmed.slice(CORRECT_MARK.length).trim(), marked: true };
}

/**
 * One option line, read both ways: as it stands, and without its letter. "*A) Книга" and "A) *Книга" are
 * both the marked option "Книга" once the letters are stripped.
 */
function readOption(raw) {
  const outer = stripCorrectMark(raw);
  const letter = outer.text.match(OPTION_LETTER_RE);
  const plain = { text: outer.text, marked: outer.marked };
  if (!letter) return { plain, lettered: null };
  const inner = stripCorrectMark(outer.text.slice(letter[0].length));
  return { plain, lettered: { text: inner.text, marked: outer.marked || inner.marked } };
}

/** Splits the text into blocks of consecutive non-blank lines, each line kept with its number. */
function toBlocks(text) {
  const blocks = [];
  let current = null;
  String(text ?? '').replace(/\r\n?/g, '\n').split('\n').forEach((raw, i) => {
    if (!raw.trim()) {
      current = null;
      return;
    }
    // A numbered line after the first one of a block starts the next question
    if (!current || QUESTION_NUMBER_RE.test(raw.trim())) {
      current = [];
      blocks.push(current);
    }
    current.push({ line: i + 1, raw });
  });
  return blocks;
}

function readBlock(block) {
  const [head, ...rest] = block;
  const errors = [];
  const headText = head.raw.trim();
  if (headText.startsWith(CORRECT_MARK)) {
    errors.push({ line: head.line, code: BULK_ERRORS.MISSING_QUESTION });
    return { errors };
  }
  const text = headText.replace(QUESTION_NUMBER_RE, '').trim();
  if (rest.length === 0) {
    errors.push({ line: head.line, code: BULK_ERRORS.NO_OPTIONS });
    return { errors };
  }

  const read = rest.map((row) => ({ ...readOption(row.raw), line: row.line }));
  // Letters are stripped only when every option has one: "А. С. Пушкин" among unlettered options is an answer
  const allLettered = read.every((o) => o.lettered);
  const options = read.map((o) => ({ line: o.line, ...(allLettered ? o.lettered : o.plain) }));

  options.filter((o) => !o.text).forEach((o) => errors.push({ line: o.line, code: BULK_ERRORS.EMPTY_OPTION }));
  if (options.length < MIN_OPTIONS) errors.push({ line: head.line, code: BULK_ERRORS.TOO_FEW_OPTIONS });
  if (options.length > MAX_OPTIONS) errors.push({ line: head.line, code: BULK_ERRORS.TOO_MANY_OPTIONS });
  const marked = options.filter((o) => o.marked);
  if (marked.length === 0) errors.push({ line: head.line, code: BULK_ERRORS.NO_CORRECT });
  if (marked.length > 1) errors.push({ line: head.line, code: BULK_ERRORS.MULTIPLE_CORRECT });

  return {
    errors,
    question: {
      line: head.line,
      text,
      options: options.map((o) => o.text),
      correctIndex: options.findIndex((o) => o.marked),
    },
  };
}

/**
 * Reads pasted quiz questions. Returns every question that was read without a problem, and the problems,
 * ordered by line. A question with a problem is left out of `questions`, so what is added is never half
 * right.
 */
export function parseBulkQuestions(text) {
  const questions = [];
  const errors = [];
  toBlocks(text).forEach((block) => {
    const result = readBlock(block);
    if (result.errors.length) errors.push(...result.errors);
    else questions.push(result.question);
  });
  errors.sort((a, b) => a.line - b.line);
  return { questions, errors };
}
